"use client";

import { createElement, useMemo, type ReactElement } from "react";
import { TimelineCompactPreview } from "./TimelineCompactPreview";
import {
  selectTimelineLayoutMetrics,
  selectTimelineSectionBoundaryIndices,
  selectTimelineSidePad,
  selectTimelineTotalEvents,
} from "./timeline-root-selectors";
import type { TimelineLayoutMetrics } from "./timeline-root-types";
import type { ParsedTimeline } from "./timeline-types";

export interface UseTimelineLayoutMetricsInput {
  collapsedSections: Set<number>;
  parsed: ParsedTimeline | null;
}

export interface UseTimelineRootMetricsInput {
  layout: TimelineLayoutMetrics | null;
  parsed: ParsedTimeline | null;
  viewportWidth: number;
}

export interface TimelineRootMetrics {
  compactPreview: ReactElement | null;
  layout: TimelineLayoutMetrics | null;
  sectionBoundaryIndices: Set<number>;
  sidePad: number;
  totalEvents: number;
}

export function useTimelineLayoutMetrics({
  collapsedSections,
  parsed,
}: UseTimelineLayoutMetricsInput): TimelineLayoutMetrics | null {
  return useMemo(
    () =>
      selectTimelineLayoutMetrics({
        collapsedSections,
        parsed,
      }),
    [collapsedSections, parsed],
  );
}

export function useTimelineRootMetrics({
  layout,
  parsed,
  viewportWidth,
}: UseTimelineRootMetricsInput): TimelineRootMetrics {
  const sidePad = useMemo(
    () =>
      selectTimelineSidePad({
        layout,
        viewportWidth,
      }),
    [layout, viewportWidth],
  );

  const sectionBoundaryIndices = useMemo(
    () => selectTimelineSectionBoundaryIndices({ parsed }),
    [parsed],
  );

  const totalEvents = useMemo(
    () => selectTimelineTotalEvents({ parsed }),
    [parsed],
  );

  const compactPreview = useMemo(() => {
    if (!parsed) {
      return null;
    }

    return createElement(TimelineCompactPreview, { periods: parsed.periods });
  }, [parsed]);

  return {
    compactPreview,
    layout,
    sectionBoundaryIndices,
    sidePad,
    totalEvents,
  };
}
